import {
  LastOrderProperty,
  TimeUnit,
  type LastOrderCondition as LastOrderConditionType,
} from "@redotech/redo-model/advanced-flow/rules-v3/schemas";
import {
  DateQueryOperator,
  NumericQueryOperator,
} from "@redotech/redo-model/advanced-flow/rules/types";
import { RedoSingleDatePicker } from "@redotech/redo-web/arbiter-components/date-picker/redo-single-date-picker";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useCallback, useMemo } from "react";

const propertyOptions = [
  {
    id: LastOrderProperty.TIME,
    type: "text" as const,
    text: "Order time",
    value: LastOrderProperty.TIME,
  },
  {
    id: LastOrderProperty.VALUE,
    type: "text" as const,
    text: "Order value",
    value: LastOrderProperty.VALUE,
  },
  {
    id: LastOrderProperty.ITEM_COUNT,
    type: "text" as const,
    text: "Number of items",
    value: LastOrderProperty.ITEM_COUNT,
  },
];

const canEdit = (
  editableFields: ReadonlySet<string> | undefined,
  field: string,
) => editableFields === undefined || editableFields.has(field);

export const LastOrderCondition = memo(function LastOrderCondition({
  condition,
  onChange,
  editableFields,
}: {
  condition: LastOrderConditionType;
  onChange: (updated: LastOrderConditionType) => void;
  editableFields?: ReadonlySet<string>;
}) {
  const propertyEditable = canEdit(editableFields, "property");

  const handlePropertyChange = useCallback(
    (property: LastOrderProperty) => {
      switch (property) {
        case LastOrderProperty.TIME:
          onChange({
            conditionType: condition.conditionType,
            property: LastOrderProperty.TIME,
            queryOperator: DateQueryOperator.WITHIN_LAST,
            unit: TimeUnit.DAYS,
          });
          break;
        case LastOrderProperty.VALUE:
          onChange({
            conditionType: condition.conditionType,
            property: LastOrderProperty.VALUE,
            queryOperator: NumericQueryOperator.GREATER_THAN,
            value: 0,
          });
          break;
        case LastOrderProperty.ITEM_COUNT:
          onChange({
            conditionType: condition.conditionType,
            property: LastOrderProperty.ITEM_COUNT,
            queryOperator: NumericQueryOperator.GREATER_THAN,
            value: 0,
          });
          break;
      }
    },
    [condition.conditionType, onChange],
  );

  const selectedProperty = useMemo(
    () => propertyOptions.find((opt) => opt.value === condition.property),
    [condition.property],
  );

  return (
    <Flex dir="column" gap="md" w="full">
      <RedoSingleSelectDropdownInput
        disabled={!propertyEditable}
        options={propertyOptions}
        optionSelected={({ item }) => {
          if (!propertyEditable) return;
          handlePropertyChange(item.value);
        }}
        placeholder="Select property"
        selectedItem={selectedProperty}
      />
      {condition.property === LastOrderProperty.TIME && (
        <TimeInputs
          condition={condition}
          editableFields={editableFields}
          onChange={onChange}
        />
      )}
      {(condition.property === LastOrderProperty.VALUE ||
        condition.property === LastOrderProperty.ITEM_COUNT) && (
        <NumericInputs
          condition={condition}
          editableFields={editableFields}
          onChange={onChange}
          unit={condition.property === LastOrderProperty.VALUE ? "$" : "items"}
        />
      )}
    </Flex>
  );
});

const timeUnitOptions = [
  {
    id: TimeUnit.MINUTES,
    type: "text" as const,
    text: "minutes",
    value: TimeUnit.MINUTES,
  },
  {
    id: TimeUnit.HOURS,
    type: "text" as const,
    text: "hours",
    value: TimeUnit.HOURS,
  },
  {
    id: TimeUnit.DAYS,
    type: "text" as const,
    text: "days",
    value: TimeUnit.DAYS,
  },
];

const TimeInputs = memo(function TimeInputs({
  condition,
  onChange,
  editableFields,
}: {
  condition: Extract<LastOrderConditionType, { property: "time" }>;
  onChange: (updated: LastOrderConditionType) => void;
  editableFields?: ReadonlySet<string>;
}) {
  const valueEditable = canEdit(editableFields, "value");
  const unitEditable = canEdit(editableFields, "unit");
  const dateEditable = canEdit(editableFields, "date");
  const startDateEditable = canEdit(editableFields, "startDate");
  const endDateEditable = canEdit(editableFields, "endDate");

  const parseDate = (
    dateStr: string | undefined,
  ): Temporal.PlainDate | undefined => {
    if (!dateStr) {
      return undefined;
    }
    try {
      return Temporal.PlainDate.from(dateStr);
    } catch {
      return undefined;
    }
  };

  switch (condition.queryOperator) {
    case DateQueryOperator.WITHIN_LAST:
      return (
        <Flex align="center" gap="sm">
          <RedoTextInput
            inputTypeHint="number"
            min={1}
            placeholder="0"
            setValue={(val: string) => {
              if (!valueEditable) return;
              const num = parseInt(val, 10);
              onChange({ ...condition, value: isNaN(num) ? undefined : num });
            }}
            size="sm"
            state={valueEditable ? "default" : "disabled"}
            value={condition.value?.toString() ?? ""}
          />
          <RedoSingleSelectDropdownInput
            disabled={!unitEditable}
            options={timeUnitOptions}
            optionSelected={({ item }) => {
              if (!unitEditable) return;
              onChange({ ...condition, unit: item.value });
            }}
            selectedItem={timeUnitOptions.find(
              (opt) => opt.value === (condition.unit ?? TimeUnit.DAYS),
            )}
          />
        </Flex>
      );

    case DateQueryOperator.ON_OR_BEFORE:
    case DateQueryOperator.ON_OR_AFTER:
      return (
        <RedoSingleDatePicker
          buttonSize="sm"
          date={parseDate(condition.date)}
          disabled={!dateEditable}
          setDate={(date: Temporal.PlainDate) => {
            if (!dateEditable) return;
            onChange({ ...condition, date: date.toString() });
          }}
        />
      );

    case DateQueryOperator.BETWEEN:
      return (
        <Flex align="center" gap="sm">
          <RedoSingleDatePicker
            buttonSize="sm"
            date={parseDate(condition.startDate)}
            disabled={!startDateEditable}
            setDate={(date: Temporal.PlainDate) => {
              if (!startDateEditable) return;
              onChange({ ...condition, startDate: date.toString() });
            }}
          />
          <Text color="secondary" fontSize="sm">
            and
          </Text>
          <RedoSingleDatePicker
            buttonSize="sm"
            date={parseDate(condition.endDate)}
            disabled={!endDateEditable}
            setDate={(date: Temporal.PlainDate) => {
              if (!endDateEditable) return;
              onChange({ ...condition, endDate: date.toString() });
            }}
          />
        </Flex>
      );

    case DateQueryOperator.NEVER:
      return null;

    default:
      return null;
  }
});

const NumericInputs = memo(function NumericInputs({
  condition,
  onChange,
  unit,
  editableFields,
}: {
  condition: Extract<
    LastOrderConditionType,
    { property: "value" | "item_count" }
  >;
  onChange: (updated: LastOrderConditionType) => void;
  unit: string;
  editableFields?: ReadonlySet<string>;
}) {
  const valueEditable = canEdit(editableFields, "value");
  const minValueEditable = canEdit(editableFields, "minValue");
  const maxValueEditable = canEdit(editableFields, "maxValue");

  switch (condition.queryOperator) {
    case NumericQueryOperator.GREATER_THAN:
    case NumericQueryOperator.LESS_THAN:
    case NumericQueryOperator.EQUAL_TO:
      return (
        <Flex align="center" gap="sm">
          {unit === "$" && (
            <Text color="secondary" fontSize="sm">
              $
            </Text>
          )}
          <RedoTextInput
            inputTypeHint="number"
            min={0}
            placeholder="0"
            setValue={(value: string) => {
              if (!valueEditable) return;
              const num = parseFloat(value);
              onChange({ ...condition, value: isNaN(num) ? 0 : num });
            }}
            size="sm"
            state={valueEditable ? "default" : "disabled"}
            value={condition.value.toString()}
          />
          {unit !== "$" && (
            <Text color="secondary" fontSize="sm">
              {unit}
            </Text>
          )}
        </Flex>
      );

    case NumericQueryOperator.BETWEEN:
      return (
        <Flex align="center" gap="sm">
          {unit === "$" && (
            <Text color="secondary" fontSize="sm">
              $
            </Text>
          )}
          <RedoTextInput
            inputTypeHint="number"
            min={0}
            placeholder="0"
            setValue={(value: string) => {
              if (!minValueEditable) return;
              const num = parseFloat(value);
              onChange({ ...condition, minValue: isNaN(num) ? 0 : num });
            }}
            size="sm"
            state={minValueEditable ? "default" : "disabled"}
            value={condition.minValue.toString()}
          />
          <Text color="secondary" fontSize="sm">
            and
          </Text>
          {unit === "$" && (
            <Text color="secondary" fontSize="sm">
              $
            </Text>
          )}
          <RedoTextInput
            inputTypeHint="number"
            min={0}
            placeholder="0"
            setValue={(value: string) => {
              if (!maxValueEditable) return;
              const num = parseFloat(value);
              onChange({ ...condition, maxValue: isNaN(num) ? 0 : num });
            }}
            size="sm"
            state={maxValueEditable ? "default" : "disabled"}
            value={condition.maxValue.toString()}
          />
          {unit !== "$" && (
            <Text color="secondary" fontSize="sm">
              {unit}
            </Text>
          )}
        </Flex>
      );

    default:
      return null;
  }
});
